<template>
  <!-- The tenant's side of the sign-in page. Every colour is a tenant token:
       the deep background (branding secondary), the lines, tiles and eyebrow
       mark (branding tertiary). Decorative layers are aria-hidden. -->
  <section class="si-panel" :style="{ '--si-deep': deep }" data-test="field-panel">
    <svg class="si-panel__contour" viewBox="0 0 800 900" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <path
        v-for="(line, i) in CONTOURS"
        :key="i"
        :d="line"
        :class="i % 2 === 0 ? 'si-panel__line--accent' : 'si-panel__line--soft'"
      />
    </svg>
    <div class="si-panel__scan" aria-hidden="true"></div>

    <header class="si-panel__brand">
      <LogoTile :branding="branding" :alt="brand" :size="40" />
      <span class="si-panel__name">{{ brand }}</span>
    </header>

    <div class="si-panel__copy">
      <p class="si-panel__eyebrow">{{ eyebrow }}</p>
      <h1 class="si-panel__headline">{{ headline }}</h1>
      <p v-if="lede" class="si-panel__lede">{{ lede }}</p>
    </div>

    <div class="si-panel__foot">
      <div class="si-panel__tiles" aria-hidden="true">
        <span
          v-for="cell in CELLS"
          :key="cell.index"
          class="si-panel__cell"
          :class="{ 'si-panel__cell--accent': cell.accent }"
          :style="{ animationDelay: cell.delay }"
        ></span>
      </div>
      <ul v-if="chips.length" class="si-panel__chips">
        <li v-for="chip in chips" :key="chip" class="si-panel__chip">{{ chip }}</li>
      </ul>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import type { TenantBranding } from "@tenant-theme";
import LogoTile from "@/shell/LogoTile.vue";

/** Nine contour lines across an 800 × 900 field; the stroke dashes drift along them. */
const CONTOURS = [
  "M-40 120 C 160 60 320 180 520 120 S 860 40 900 110",
  "M-40 200 C 140 150 340 260 540 200 S 860 130 900 190",
  "M-40 290 C 180 240 360 350 560 280 S 860 220 900 270",
  "M-40 380 C 120 330 380 450 580 370 S 860 300 900 360",
  "M-40 470 C 160 420 400 540 600 460 S 860 400 900 450",
  "M-40 560 C 200 520 420 630 620 550 S 860 490 900 540",
  "M-40 650 C 180 600 440 720 640 640 S 860 580 900 630",
  "M-40 740 C 220 700 460 810 660 730 S 860 670 900 720",
  "M-40 830 C 200 790 480 900 680 820 S 860 760 900 810",
];

/** 36 tiles (3 rows of 12). A fixed pattern, so the panel looks the same on every load. */
const CELLS = Array.from({ length: 36 }, (_, index) => ({
  index,
  accent: (index * 7) % 5 === 0,
  delay: `${((index * 37) % 32) / 10}s`,
}));

export default defineComponent({
  name: "FieldPanel",
  components: { LogoTile },
  props: {
    brand: { type: String, required: true },
    branding: { type: Object as PropType<TenantBranding | null>, default: null },
    /** The panel background. A CSS colour; the parent makes sure white text reads on it. */
    deep: { type: String, required: true },
    eyebrow: { type: String, required: true },
    headline: { type: String, required: true },
    lede: { type: String, default: "" },
    chips: { type: Array as PropType<string[]>, default: () => [] },
  },
  setup() {
    return { CONTOURS, CELLS };
  },
});
</script>

<style scoped>
.si-panel {
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 32px;
  flex: 56 1 0;
  min-width: 0;
  min-height: 560px;
  padding: max(48px, env(safe-area-inset-top, 0px)) 56px 48px max(56px, env(safe-area-inset-left, 0px));
  box-sizing: border-box;
  background: var(--si-deep);
  color: #fff;
}
.si-panel__contour {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0.55;
}
.si-panel__contour path {
  fill: none;
  stroke-width: 1.2;
  stroke-dasharray: 6 10;
}
.si-panel__line--accent {
  stroke: var(--tenant-tertiary);
  stroke-opacity: 0.6;
}
.si-panel__line--soft {
  stroke: #fff;
  stroke-opacity: 0.3;
}
.si-panel__scan {
  position: absolute;
  inset: 0 0 auto;
  height: 160px;
  background: linear-gradient(180deg, transparent, var(--tenant-tertiary), transparent);
  opacity: 0.14;
  pointer-events: none;
}
.si-panel__brand,
.si-panel__copy,
.si-panel__foot {
  position: relative;
}
.si-panel__brand {
  display: flex;
  align-items: center;
  gap: 12px;
}
.si-panel__name {
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.3px;
}
.si-panel__copy {
  max-width: 520px;
}
/* White text, not the accent: an accent from the tenant palette is not sure
   to reach 4.5:1 on the deep colour. The accent shows as the bar before it. */
.si-panel__eyebrow {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 16px;
  font-family: var(--si-mono);
  font-size: 12px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.82);
}
.si-panel__eyebrow::before {
  content: "";
  width: 18px;
  height: 2px;
  border-radius: 2px;
  background: var(--tenant-tertiary);
}
.si-panel__headline {
  margin: 0 0 20px;
  font-size: clamp(40px, 5vw, 64px);
  line-height: 1.02;
  font-weight: 700;
  letter-spacing: -2px;
  white-space: pre-line;
  overflow-wrap: anywhere;
}
.si-panel__lede {
  margin: 0;
  font-size: 18px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.82);
}
.si-panel__foot {
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.si-panel__tiles {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 6px;
  max-width: 420px;
}
.si-panel__cell {
  height: 14px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.35);
  opacity: 0.6;
}
.si-panel__cell--accent {
  background: var(--tenant-tertiary);
}
.si-panel__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.si-panel__chip {
  padding: 8px 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 500;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.22);
}

/* Motion only while the page says so (SignInSplit drops the class for reduced motion). */
.si-motion .si-panel__contour path {
  animation: si-contour 60s linear infinite;
}
.si-motion .si-panel__scan {
  animation: si-scan 7s cubic-bezier(0.6, 0, 0.4, 1) infinite;
}
.si-motion .si-panel__cell {
  animation: si-pulse 3.2s ease-in-out infinite;
}
@keyframes si-contour {
  to {
    stroke-dashoffset: -1200;
  }
}
@keyframes si-scan {
  from {
    transform: translateY(-10%);
  }
  to {
    transform: translateY(560%);
  }
}
@keyframes si-pulse {
  0%,
  100% {
    opacity: 0.25;
  }
  50% {
    opacity: 1;
  }
}

/* Phone and small tablet: a short header band over the form. */
@media (max-width: 900px) {
  .si-panel {
    flex: 0 0 auto;
    min-height: 0;
    gap: 16px;
    padding: max(20px, env(safe-area-inset-top, 0px)) max(20px, env(safe-area-inset-right, 0px)) 24px
      max(20px, env(safe-area-inset-left, 0px));
  }
  .si-panel__eyebrow,
  .si-panel__lede,
  .si-panel__foot {
    display: none;
  }
  .si-panel__headline {
    margin: 0;
    font-size: 26px;
    letter-spacing: -0.6px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

@media (prefers-reduced-motion: reduce) {
  .si-panel__contour path,
  .si-panel__scan,
  .si-panel__cell {
    animation: none !important;
  }
}
</style>
